// ─────────────────────────────────────────────────────────────────────────────
// ExternalReferencesEffects
// ─────────────────────────────────────────────────────────────────────────────
// NgRx effects for external references CRUD operations.
// Responsibilities:
//   - Load paginated external references list from API
//   - Create, update, delete, and bulk-delete external references
//   - Manage loading state via SetExternalReferencesLoader
//   - Show toast notifications on success/error
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { APIResponse } from 'src/app/core/models/api-response';
import { CommonService } from 'src/app/shared/common.service';
import { TranslateService } from '@ngx-translate/core';
import { ExternalReferenceService } from '../services/external-reference.service';
import { IExternalReference } from '../models/external-reference.models';
import * as externalReferencesActions from './external-references.actions';

@Injectable()
export class ExternalReferencesEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private externalReferenceService: ExternalReferenceService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  // ── Load external references ──────────────────────────────────────────────

  LoadExternalReferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(externalReferencesActions.LoadExternalReferences),
      switchMap((action) =>
        this.externalReferenceService.listExternalReferences(action.page, action.key, action.filters, action.sortBy, action.sortMode).pipe(
          map((res: APIResponse<IExternalReference>) => externalReferencesActions.LoadExternalReferencesSuccess({ response: res })),
          catchError(() => of(externalReferencesActions.LoadExternalReferencesError())),
        ),
      ),
    ),
  );

  // ── Create external reference ─────────────────────────────────────────────

  CreateExternalReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(externalReferencesActions.CreateExternalReference),
      tap(() => {
        this.store.dispatch(externalReferencesActions.SetExternalReferencesLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.externalReferenceService.createExternalReference(action.payload).pipe(
          map((res: APIResponse<IExternalReference>) => externalReferencesActions.CreateExternalReferenceSuccess({ reference: res.data[0] })),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.external_references.toast.create_success'), 'SUCCESS');
          }),
          catchError(() => of(externalReferencesActions.CreateExternalReferenceError())),
          finalize(() => {
            this.store.dispatch(externalReferencesActions.SetExternalReferencesLoader({ isLoading: false }));
          }),
        ),
      ),
    ),
  );

  // ── Update external reference ─────────────────────────────────────────────

  UpdateExternalReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(externalReferencesActions.UpdateExternalReference),
      tap(() => {
        this.store.dispatch(externalReferencesActions.SetExternalReferencesLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.externalReferenceService.updateExternalReference(action.id, action.payload).pipe(
          map((res: APIResponse<IExternalReference>) => externalReferencesActions.UpdateExternalReferenceSuccess({ reference: res.data[0] })),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.external_references.toast.update_success'), 'SUCCESS');
          }),
          catchError(() => of(externalReferencesActions.UpdateExternalReferenceError())),
          finalize(() => {
            this.store.dispatch(externalReferencesActions.SetExternalReferencesLoader({ isLoading: false }));
          }),
        ),
      ),
    ),
  );

  // ── Delete external reference ─────────────────────────────────────────────

  DeleteExternalReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(externalReferencesActions.DeleteExternalReference),
      tap(() => {
        this.store.dispatch(externalReferencesActions.SetExternalReferencesLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.externalReferenceService.deleteExternalReference(action.id).pipe(
          map(() => externalReferencesActions.DeleteExternalReferenceSuccess()),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.external_references.toast.delete_success'), 'SUCCESS');
          }),
          catchError(() => of(externalReferencesActions.DeleteExternalReferenceError())),
          finalize(() => {
            this.store.dispatch(externalReferencesActions.SetExternalReferencesLoader({ isLoading: false }));
          }),
        ),
      ),
    ),
  );

  // ── Bulk delete external references ──────────────────────────────────────

  BulkDeleteExternalReferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(externalReferencesActions.BulkDeleteExternalReferences),
      tap(() => {
        this.store.dispatch(externalReferencesActions.SetExternalReferencesLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.externalReferenceService.bulkDeleteExternalReferences(action.ids).pipe(
          map(() => externalReferencesActions.BulkDeleteExternalReferencesSuccess()),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.external_references.toast.bulk_delete_success'), 'SUCCESS');
          }),
          catchError(() => of(externalReferencesActions.BulkDeleteExternalReferencesError())),
          finalize(() => {
            this.store.dispatch(externalReferencesActions.SetExternalReferencesLoader({ isLoading: false }));
          }),
        ),
      ),
    ),
  );
}
